import { CommonModule } from "@angular/common";
import { Component, Input, SimpleChanges } from "@angular/core";
import { MindMapNode } from "../../../core/models/mind-map-node";

/**
 * This component is responsible for rendering a mind map with nodes representing various issue types
 * and their associated counts. The map dynamically adjusts based on input data and calculates percentages
 * for each issue type relative to the total count of issues.
 */
@Component({
  selector: "app-mind-map",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./mind-map.component.html",
  styleUrl: "./mind-map.component.scss",
})
export class MindMapComponent {
  @Input() data: any;

  issueSummary: { [key: string]: number } = {};
  NODE_DATA: MindMapNode[] = [];
  parentNodeY: any;
  xOffset: number = 0;
  yOffset: number = 0;
  currentY: number = 0;
  nodes: MindMapNode[] = [];
  midValue: number = 0;
  hoveredNode: any = null;
  offset: number = 0;
  width: number = 600;
  height: number = 700;

  /**
   * Lifecycle hook that is called when the input data changes. It reinitializes the mind map data
   * if the `data` input property changes.
   * @param changes - An object that contains the current and previous values of the `@Input` properties.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes["data"]) {
      this.data = changes["data"].currentValue;
      if (this.data) {
        this.buildData();
      }
    }
  }

  /**
   * This method processes the input `data`, calculates the issue summary, and builds the node structure
   * for the mind map. It creates a parent node for the total defects and child nodes for each issue type,
   * displaying both the count and percentage for each issue type.
   */
  buildData() {
    this.xOffset = 400;
    this.yOffset = 35;
    this.currentY = 300;
    this.parentNodeY = 300;
    this.issueSummary = {};
    this.nodes = [];

    this.data?.forEach((item: any) => {
      const issueType = Object.keys(item).filter((key) => key !== "suite")[0];
      const issueCount = item[issueType];

      if (this.issueSummary[issueType]) {
        this.issueSummary[issueType] += issueCount;
      } else {
        this.issueSummary[issueType] = issueCount;
      }
    });

    this.midValue = Object.keys(this.issueSummary).length / 2;

    const sum = Object.values(this.issueSummary).reduce(
      (sum, value) => sum + value,
      0
    );

    this.NODE_DATA.push({
      id: 1,
      label: "Total Defects ",
      x: 70,
      y: this.parentNodeY,
      shape: "square",
      value: sum,
    });

    let nodeId = 2; // Start child node IDs from 2

    if (Number.isInteger(this.midValue)) {
      this.offset = 0;
    } else {
      this.offset = 20;
    }

    Object.keys(this.issueSummary).forEach((issueType, index) => {
      // Initialize childY based on whether the node is in the upper or lower half
      let childY;

      if (index < this.midValue || index == this.midValue) {
        // Upper half: Start from the currentY and add yOffset to avoid overlap
        childY = this.currentY + index * this.yOffset + this.offset;
      } else {
        // Lower half: Start from currentY and subtract yOffset to avoid overlap
        childY = this.currentY - (index - this.midValue) * this.yOffset;
      }

      let percentage = parseFloat(
        ((this.issueSummary[issueType] / sum) * 100).toFixed(2)
      );
      // Push the node into NODE_DATA
      this.NODE_DATA.push({
        id: nodeId,
        label: issueType,
        parentId: 1,
        x: this.xOffset,
        y: childY,
        shape: "oval",
        value: this.issueSummary[issueType],
        percentage: percentage,
      });

      nodeId++;
    });

    this.nodes = this.NODE_DATA;
  }

  /**
   * This method returns the parent node for a given node.
   * @param node - The node whose parent is being requested.
   * @returns The parent node corresponding to the provided node.
   */
  getParentNode(node: MindMapNode): MindMapNode | undefined {
    return this.nodes.find((n) => n.id === node.parentId);
  }

  /**
   * Lifecycle hook that is called once when the component is initialized. It builds the data for the mind map.
   */
  ngOnInit(): void {
    this.buildData();
  }

  /**
   * This method is triggered when the user hovers over a node in the mind map.
   * It sets the `hoveredNode` property to the node being hovered over.
   * @param node - The node being hovered over.
   */
  onNodeHover(node: any) {
    this.hoveredNode = node; // Set the hovered node data
  }

  /**
   * This method is triggered when the user moves the mouse away from a node.
   * It clears the `hoveredNode` property.
   */
  onNodeLeave() {
    this.hoveredNode = null; // Clear the hovered node data
  }
}
